import React, { useState, useEffect } from "react";
import {
  Modal,
  Button,
  Form,
  Alert,
  Spinner,
  Row,
  Col,
} from "react-bootstrap";
import { API_URL } from "utils/constants";

const ImageUploadModal = ({
  item,
  show,
  onHide,
  onSuccess,
  onError,
}) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Initialize with existing image if available
  useEffect(() => {
    if (item?.image_url) {
      setPreview(item.image_url);
    } else {
      setPreview(null);
    }
    setSelectedFile(null);
    setError("");
  }, [item, show]);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        setError("Please select a valid image file");
        return;
      }

      // Validate file size (5MB limit)
      if (file.size > 5 * 1024 * 1024) {
        setError("File size must be less than 5MB");
        return;
      }

      setSelectedFile(file);
      setError("");

      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => setPreview(e.target.result);
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!selectedFile) {
      setError("Please select an image file");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const formData = new FormData();
      formData.append("image", selectedFile);

      const response = await fetch(`${API_URL}/inventory/${item.id}/image`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: formData,
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      if (onSuccess) {
        onSuccess(result);
      }

      // Close modal
      if (onHide) {
        onHide();
      }
    } catch (error) {
      console.error("Error uploading image:", error);
      const errorMessage = error.message || "Failed to upload image";
      setError(errorMessage);

      if (onError) {
        onError(error);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setSelectedFile(null);
    setPreview(null);
    setError("");
    if (onHide) {
      onHide();
    }
  };

  return (
    <Modal show={show} onHide={handleClose} size="md" centered className="workshop-modal-scope">
      <Modal.Header closeButton>
        <Modal.Title>Upload Image - {item?.name}</Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          {error && (
            <Alert variant="danger" className="mb-3">
              {error}
            </Alert>
          )}

          <Row>
            <Col md={12}>
              <Form.Group className="mb-3">
                <Form.Label>Select Image *</Form.Label>
                <Form.Control
                  type="file"
                  accept="image/*"
                  onChange={handleFileChange}
                  required
                />
                <Form.Text className="text-muted">
                  Supported formats: JPG, PNG, GIF. Max size: 5MB
                </Form.Text>
              </Form.Group>
            </Col>
          </Row>

          {preview && (
            <Row>
              <Col md={12} className="text-center">
                <div className="mb-3">
                  <strong>Preview:</strong>
                </div>
                <img
                  src={preview}
                  alt="Preview"
                  style={{
                    maxWidth: "100%",
                    maxHeight: "300px",
                    objectFit: "contain",
                    borderRadius: "8px",
                    border: "1px solid #e5e7eb",
                    boxShadow: "0 2px 4px rgba(0,0,0,0.1)"
                  }}
                />
              </Col>
            </Row>
          )}

          {item?.image_url && !selectedFile && (
            <Row>
              <Col md={12} className="text-center">
                <div className="mb-3">
                  <strong>Current Image:</strong>
                </div>
                <img
                  src={item.image_url}
                  alt="Current"
                  style={{
                    maxWidth: "100%",
                    maxHeight: "300px",
                    objectFit: "contain",
                    borderRadius: "8px",
                    border: "1px solid #e5e7eb",
                    boxShadow: "0 2px 4px rgba(0,0,0,0.1)"
                  }}
                />
              </Col>
            </Row>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleClose}>
            Cancel
          </Button>
          <Button variant="primary" type="submit" disabled={loading || !selectedFile}>
            {loading && <Spinner size="sm" className="me-2" />}
            Upload Image
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default ImageUploadModal;
